import React, { useCallback, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { FiAward, FiClock, FiCreditCard, FiExternalLink, FiFileText, FiLayers, FiPackage, FiRefreshCw } from "react-icons/fi";
import { SecondaryButton, Section } from "../organisation/OrgModal";
import { MiniPager, Pill, Skeleton } from "./subscriptionUi";
import { apiErrorMessage, fetchPayments, fetchPlanHistory, formatCents, formatDate, metricForAddon, metricLabel, runningPlanSpan } from "../../utils/subscriptionData";

const PAGE_SIZE = 5;
// Every row is the same height and the panel never gets shorter than three rows, so loading, an empty
// list and switching tabs all occupy the same space and nothing below jumps.
const ROW = "min-h-[2.75rem]";
const PANEL = "min-h-[8.25rem]";

const REASONS = {
  trial_expired: "Trial ended",
  canceled: "Cancelled",
  payment_failed: "Payment failed",
  expired: "Expired",
  upgraded: "Upgraded",
  downgraded: "Downgraded",
  replaced: "Replaced",
};

const PAYMENT_TYPES = { plan: "Plan", renewal: "Renewal", addon: "Add-on", invoice: "Invoice" };
const PAYMENT_ICONS = { plan: FiAward, renewal: FiRefreshCw, addon: FiPackage, invoice: FiFileText };
const PAYMENT_TONES = { paid: "green", failed: "amber", open: "blue" };

const describePayment = (payment) => {
  if (payment.type === "addon") {
    const metric = metricForAddon(payment.addon_type);
    return `${payment.quantity ?? 1} × ${metric ? metricLabel(metric) : payment.addon_type || "add-on"}`;
  }
  return payment.plan_name || PAYMENT_TYPES[payment.type] || "Payment";
};

const IconTile = ({ icon: Icon }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
    <Icon className="h-3.5 w-3.5" />
  </span>
);

const SkeletonRows = () => (
  <ul role="status" aria-label="Loading" className="divide-y">
    {[0, 1, 2].map((row) => (
      <li key={row} className={`flex items-center gap-3 px-1 py-1 ${ROW}`}>
        <Skeleton className="h-7 w-7 rounded-lg" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-28" />
        </div>
        <Skeleton className="h-4 w-16" />
      </li>
    ))}
  </ul>
);

// Centered inside the same minimum height as a full page of rows
const PanelMessage = ({ icon: Icon, title, children, action }) => (
  <div className={`flex flex-col items-center justify-center gap-1 text-center ${PANEL}`}>
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
      <Icon className="h-4 w-4" />
    </span>
    <p className="text-sm font-medium">{title}</p>
    {children && <p className="max-w-xs text-xs text-accent-gray-light">{children}</p>}
    {action}
  </div>
);

const PaymentRow = ({ payment }) => {
  const Icon = PAYMENT_ICONS[payment.type] || FiCreditCard;
  const paid = payment.status === "paid";
  return (
    <li className={`flex items-center gap-3 px-1 py-1 ${ROW}`}>
      <IconTile icon={Icon} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{describePayment(payment)}</p>
        <p className="truncate text-xs text-accent-gray-light">
          {PAYMENT_TYPES[payment.type] || payment.type} · {formatDate(payment.paid_at || payment.created_at)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:gap-3">
        <span className="text-sm font-semibold tabular-nums">{formatCents(paid ? payment.amount_paid_cents : payment.amount_due_cents, payment.currency)}</span>
        <Pill tone={PAYMENT_TONES[payment.status] || "gray"}>{payment.status}</Pill>
      </div>
      {payment.hosted_invoice_url ? (
        <Tooltip title={`Invoice ${payment.invoice_number || ""}`.trim()} arrow>
          <a
            href={payment.hosted_invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Invoice ${payment.invoice_number || ""} (opens in a new tab)`.replace("  ", " ")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-accent-blue hover:bg-[#eef3fb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
          >
            <FiExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </Tooltip>
      ) : (
        <Tooltip title="No invoice for this payment. Receipts are in Manage Plan." arrow>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center text-gray-300" tabIndex={0} aria-label="No invoice link. Receipts are in Manage Plan.">
            <FiFileText className="h-4 w-4" aria-hidden="true" />
          </span>
        </Tooltip>
      )}
    </li>
  );
};

const PlanRow = ({ plan }) => (
  <li className={`flex items-center gap-3 px-1 py-1 ${ROW}`}>
    <IconTile icon={FiLayers} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium">{plan.name}</p>
      <p className="truncate text-xs text-accent-gray-light">
        {plan.active ? runningPlanSpan(plan) : `${formatDate(plan.start_at)} to ${formatDate(plan.end_at)}`}
      </p>
    </div>
    {plan.active ? <Pill tone="blue">Current</Pill> : plan.ended_reason ? <Pill tone="gray">{REASONS[plan.ended_reason] || plan.ended_reason}</Pill> : null}
  </li>
);

const Tabs = ({ tab, onChange, counts }) => {
  const items = [
    { id: "payments", label: "Payments", icon: FiCreditCard },
    { id: "plans", label: "Plan history", icon: FiClock },
  ];
  const onKeys = (event) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const next = items.find((item) => item.id !== tab);
    onChange(next.id);
    document.getElementById(`history-tab-${next.id}`)?.focus();
  };
  return (
    <div role="tablist" aria-label="Billing history" onKeyDown={onKeys} className="inline-flex gap-1 rounded-xl bg-[#dbe7fb] p-1">
      {items.map(({ id, label, icon: Icon }) => {
        const selected = tab === id;
        return (
          <button
            key={id}
            id={`history-tab-${id}`}
            role="tab"
            type="button"
            aria-selected={selected}
            aria-controls={`history-panel-${id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(id)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-black ${
              selected ? "bg-accent-blue font-semibold text-white shadow-[0_2px_6px_rgba(34,82,165,0.35)]" : "font-medium text-[#2a4a86] hover:bg-white/70 hover:text-accent-blue"
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
            {counts[id] > 0 && <span className={`rounded-full px-1.5 text-[11px] leading-4 ${selected ? "bg-white text-accent-blue" : "bg-white/80 text-[#2a4a86]"}`}>{counts[id]}</span>}
          </button>
        );
      })}
    </div>
  );
};

// Payments with invoice links, and the plans the organisation has had. Both load once, up front, so
// switching tabs is instant, and paging keeps the old rows on screen (dimmed) until the new ones arrive.
const BillingHistory = () => {
  const [tab, setTab] = useState("payments");
  const [page, setPage] = useState(1);
  const [payments, setPayments] = useState({ rows: [], total: 0, loaded: false, fetching: true, error: "" });
  const [plans, setPlans] = useState({ rows: [], loaded: false, error: "" });

  const loadPayments = useCallback(() => {
    setPayments((previous) => ({ ...previous, fetching: true, error: "" }));
    fetchPayments({ page, limit: PAGE_SIZE })
      .then((body) => setPayments({ rows: body.payments || [], total: body.total || 0, loaded: true, fetching: false, error: "" }))
      .catch((err) => setPayments((previous) => ({ ...previous, loaded: true, fetching: false, error: apiErrorMessage(err, "Could not load payments.") })));
  }, [page]);

  const loadPlans = useCallback(() => {
    setPlans((previous) => ({ ...previous, error: "" }));
    fetchPlanHistory()
      .then((rows) => setPlans({ rows, loaded: true, error: "" }))
      .catch((err) => setPlans({ rows: [], loaded: true, error: apiErrorMessage(err, "Could not load plan history.") }));
  }, []);

  useEffect(loadPayments, [loadPayments]);
  useEffect(loadPlans, [loadPlans]);

  const pages = Math.max(1, Math.ceil(payments.total / PAGE_SIZE));
  const first = payments.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(page * PAGE_SIZE, payments.total);

  const retry = (load) => (
    <SecondaryButton onClick={load} sx={{ mt: 1 }}>
      Try again
    </SecondaryButton>
  );

  const paymentsPanel = () => {
    if (!payments.loaded) return <SkeletonRows />;
    if (payments.error && !payments.rows.length) return <PanelMessage icon={FiCreditCard} title={payments.error} action={retry(loadPayments)} />;
    if (!payments.rows.length) return <PanelMessage icon={FiCreditCard} title="No payments yet">Payments and invoices will appear here after your first purchase.</PanelMessage>;
    return (
      <ul className={`divide-y transition-opacity ${payments.fetching ? "opacity-50" : ""}`} aria-busy={payments.fetching}>
        {payments.rows.map((payment) => (
          <PaymentRow key={payment.id} payment={payment} />
        ))}
      </ul>
    );
  };

  const plansPanel = () => {
    if (!plans.loaded) return <SkeletonRows />;
    if (plans.error) return <PanelMessage icon={FiLayers} title={plans.error} action={retry(loadPlans)} />;
    if (!plans.rows.length) return <PanelMessage icon={FiLayers} title="No plan history yet">Your plans will be listed here as they change.</PanelMessage>;
    return (
      <ul className="divide-y">
        {plans.rows.map((plan, index) => (
          <PlanRow key={plan.id || `${plan.plan_id}-${index}`} plan={plan} />
        ))}
      </ul>
    );
  };

  return (
    <Section dense title="Billing history">
      <Tabs tab={tab} onChange={setTab} counts={{ payments: payments.total, plans: plans.rows.length }} />
      <div role="tabpanel" id={`history-panel-${tab}`} aria-labelledby={`history-tab-${tab}`} className={`mt-1.5 ${PANEL}`}>
        {tab === "payments" ? paymentsPanel() : plansPanel()}
      </div>
      {tab === "payments" && pages > 1 && (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-xs text-accent-gray-light">
          <span aria-live="polite">
            Showing {first}–{last} of {payments.total}
          </span>
          <MiniPager page={page} pages={pages} onChange={setPage} disabled={payments.fetching} />
        </div>
      )}
    </Section>
  );
};

export default BillingHistory;
